import React, { useState, useEffect } from "react";
import { Modal } from "antd";

import { StripeAccountDeletionModalText } from "./stripeAccountDeletion/StripeAccountDeletionModalText";
import { StripeAccountDeletionModalFooter } from "./stripeAccountDeletion/StripeAccountDeletionModalFooter";
import { fetchAgentPaymentsBalanceInfo, payoutById } from "../agents/api";
import { logError } from "src/appV2/lib/analytics";
import { showErrorToast, showWarningToast } from "@src/appV2/lib/Notifications";

function DeleteUserModal({ visible, onConfirm, onCancel, userId }) {
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [balance, setBalance] = useState({
    availableNow: 0,
    availableSoon: 0,
    actualBalance: 0,
  });

  async function loadBalance() {
    try {
      setPageLoading(true);
      setIsError(false);
      setBalance({
        availableNow: 0,
        availableSoon: 0,
        actualBalance: 0,
      });

      const stripeBalance = await fetchAgentPaymentsBalanceInfo({
        userId,
      });
      let actualBalance = 0;
      let negativeBalance = 0;

      if (stripeBalance?.availableSourceTypes?.card < 0) {
        negativeBalance = -stripeBalance.availableSourceTypes.card;
      }

      if (stripeBalance?.availableSourceTypes?.bank_account < 0) {
        negativeBalance = -stripeBalance.availableSourceTypes.bank_account;
      }
      actualBalance = stripeBalance.availableNow - negativeBalance;
      setBalance({ ...stripeBalance, actualBalance });
    } catch (error: unknown) {
      logError("Error in DeleteUserModal - loadBalance", { error });
      showErrorToast("Error occurred while validating the user's stripe balance, please try again");
      setIsError(true);
    } finally {
      setPageLoading(false);
      setLoading(false);
    }
  }

  useEffect(() => {
    if (visible && userId) {
      void loadBalance();
    }
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, visible]);

  const payoutBalance = async () => {
    try {
      if (balance?.availableSoon > 0) {
        showWarningToast("HCP has pending withdrawal in progress");
        return false;
      } else if (balance?.availableNow > 0) {
        await payoutById({ userId });
      }
      return true;
      // FIXME: Automatically disabled during ESLint config update
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      logError("Error in DeleteUserModal - payoutBalance", { error });

      const errorMessage = error?.response?.body?.innerError?.message?.message?.includes(
        "You cannot delete this HCP because"
      )
        ? error?.response?.body?.innerError?.message?.message
        : "Error occurred while processing payout";

      showErrorToast(errorMessage);
      return false;
    }
  };

  const onConfirmDeletion = async () => {
    setLoading(true);
    try {
      const payoutSuccessful = await payoutBalance();
      if (payoutSuccessful) {
        await onConfirm(balance);
      }
      setLoading(false);
      // FIXME: Automatically disabled during ESLint config update
    } catch {
      setLoading(false);
    }
  };

  const showDeleteButton = () => {
    if (balance.actualBalance < 0) {
      setBalance({ ...balance, actualBalance: 0 });
    }
    setIsError(false);
  };

  return (
    <Modal
      title={
        <div style={{ display: "flex" }}>
          <span>Are you sure you want to permanently delete this user?</span>
        </div>
      }
      onCancel={onCancel}
      visible={visible}
      closable={true}
      destroyOnClose={true}
      footer={
        <StripeAccountDeletionModalFooter
          pageLoading={pageLoading}
          isError={isError}
          loading={loading}
          showDeleteButton={showDeleteButton}
          onCancel={onCancel}
          onConfirmDeletion={onConfirmDeletion}
          balance={balance}
        />
      }
    >
      <StripeAccountDeletionModalText
        pageLoading={pageLoading}
        isError={isError}
        balance={balance}
      />
    </Modal>
  );
}

export { DeleteUserModal };
